import { faArrowsRotate, faCheckCircle, faTimes, faTimesCircle } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Modal, ButtonToolbar, Button as BButton } from 'react-bootstrap'
import { Row } from 'antd'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { testAuth } from 'api/integrations/zoom/testAuth'
import InputLabel from 'components/ui/forms/field/input-label/InputLabel'
import Button from 'design-system/button'
import {
  ButtonVariant,
  ButtonSizes,
  ButtonType,
} from 'design-system/button/src/Button'
import { IntegrationTypes, IntegrationZoom } from 'models/integrations'
import { ErrorToast, SuccessToast } from 'components/ui/toast/toastsTemplates'
import { upsertIntegration } from 'api/integrations'
import styles from './index.module.scss'

interface FormData {
  accountId: string
  clientId: string
  clientSecret: string
}

interface modalZoomProps {
  show: boolean
  onClose(): void
  integration?: IntegrationZoom
  updateIntegration(): void 
}

const ModalZoom: React.FC<modalZoomProps> = ({
  show,
  onClose,
  integration,
  updateIntegration
}) => {
  const { t } = useTranslation()
  const [checkConnection, setCheckConnection] = useState<boolean | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    getValues
  } = useForm<FormData>()

  useEffect(() => {
    if (integration) {
      const { config } = { ...integration }
      setValue('accountId', config?.accountId || '')
      setValue('clientId', config?.clientId || '')
      setValue('clientSecret', config?.clientSecret || '')
    }
  }, [integration, setValue])

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      await upsertIntegration({
        integrationType: IntegrationTypes.zoom,
        active: true,
        config: {
          accountId: data.accountId,
          clientId: data.clientId,
          clientSecret: data.clientSecret,
        },
      })
      updateIntegration()
      handleOnClose()
      SuccessToast(t('configuration.integrations.zoomModal.toastMessages.success'))
    } catch {
      ErrorToast(t('configuration.integrations.zoomModal.toastMessages.error'))
    }
  }

  const testAuthentication = async () => {
    setLoading(true)
    try {
      const data = getValues()
      const response = await testAuth({
        integrationType: IntegrationTypes.zoom,
        active: true,
        config: {
          accountId: data.accountId,
          clientId: data.clientId,
          clientSecret: data.clientSecret,
        }
      })
      if (response.status === 200) {
        setCheckConnection(true)
      }
      setLoading(false)
    } catch {
      setCheckConnection(false)
      setLoading(false)
    }
  }

  const handleOnClose = () => {
    onClose()
  }

  const chechErrors = () => {
    return (
      errors.accountId?.type === 'required' ||
      errors.clientId?.type === 'required' ||
      errors.clientSecret?.type === 'required'
    )
  }

  const renderMessageZoom = () => { 
    if (checkConnection === true) {
      return (
        <div className={styles.zoomMessagesSuccess}>
          <FontAwesomeIcon icon={faCheckCircle} color='green' className='mr-2'/>
          {t('configuration.integrations.zoomModal.succesCheckZoom')}
        </div>
      )
    }
    if (checkConnection === false) {
      return (
        <div className={styles.zoomMessagesError}>
          <FontAwesomeIcon icon={faTimesCircle} color='red' className='mr-2' />
          {t('configuration.integrations.zoomModal.errorCheckZoom')}
        </div>
      )
    }
    return null;
   }

  return (
    <Modal
      show={show}
      onHide={handleOnClose}
      size="lg"
      className={styles.modalContainer}
    >
      <Modal.Body className={'pt-0 px-0'}>
        <Row className="position-relative">
          <h1>{t('configuration.integrations.zoomModal.title')}</h1>
          <a className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
          </a>
        </Row>
        <Row>
          <form onSubmit={handleSubmit(onSubmit)}>
            <InputLabel
              label={'Account ID*'}
              className={`pt-3 ${styles.label}`}
            />
            <input
              className={`form-control ${styles.inputBorder}`}
              {...register('accountId', {
                required: true,
              })}
            />
            <InputLabel
              label={'Client ID*'}
              className={`pt-3 ${styles.label}`}
            />
            <input
              className={`form-control ${styles.inputBorder}`}
              {...register('clientId', {
                required: true,
              })}
            />
            <InputLabel
              label={'Client secret*'}
              className={`pt-3 ${styles.label}`}
            />
            <input
              className={`form-control ${styles.inputBorder}`}
              {...register('clientSecret', {
                required: true,
              })}
              type="password"
            />
            <div>
              <BButton
                className={`${styles.customButton} text-primary`}
                variant="link"
                onClick={() => testAuthentication()}
              >
                <FontAwesomeIcon icon={faArrowsRotate} className={`mr-2 ${loading && styles.spinner}`} />
                {t('configuration.integrations.zoomModal.checkConnection')}
              </BButton>
            </div>
            <div>
              {renderMessageZoom()}
            </div>

            <ButtonToolbar className="float-right pt-0">
              <Button
                variant={ButtonVariant.Secondary}
                size={ButtonSizes.Small}
                onClick={() => handleOnClose()}
              >
                {t('configuration.integrations.zoomModal.cancel')}
              </Button>
              <Button
                type={ButtonType.Submit}
                variant={ButtonVariant.Primary}
                size={ButtonSizes.Small}
                disabled={chechErrors()}
              >
                {t('configuration.integrations.zoomModal.save')}
              </Button>
            </ButtonToolbar>
          </form>
        </Row>
      </Modal.Body>
    </Modal>
  )
}

export default ModalZoom
